import { NotificationType } from '@app/components/common/BaseNotification/BaseNotification';
import { Priority } from '@app//constants/enums/priorities';
import visa from '@app/assets/images/card-issuers/visa.png';
import mastercard from '@app/assets/images/card-issuers/mastercard.png';
import maestro from '@app/assets/images/card-issuers/maestro.png';
import { CurrencyTypeEnum, Severity } from '@app/interfaces/interfaces';
import { BaseBadgeProps } from '@app/components/common/BaseBadge/BaseBadge';
import { currencies } from '@app/constants/config/currencies';
import { getMonth } from '@app/constants/YMData';
import { Dates } from '@app/constants/Dates';
import AWS from 'aws-sdk';
import { Buffer } from 'buffer';
import { onlineTasks } from '@app/constants/ui';
import { isNumber } from 'lodash';

export const camelize = (string: string): string => {
  return string
    .split(' ')
    .map((word, index) => (index === 0 ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1)))
    .join('');
};

export const getCurrencyPrice = (price: number | string, currency: CurrencyTypeEnum, isIcon = true): string => {
  const currencySymbol = currencies[currency][isIcon ? 'icon' : 'text'];

  return isIcon ? `${currencySymbol}${price}` : `${price} ${currencySymbol}`;
};

type MarkArea = {
  xAxis: number;
};

export const getMarkAreaData = (data: string[] | number[]): MarkArea[][] =>
  data.map((el, index) => [
    {
      xAxis: 2 * index,
    },
    {
      xAxis: 2 * index + 1,
    },
  ]);

export const capitalize = (word: string): string => `${word[0].toUpperCase()}${word.slice(1)}`;

export const hexToRGB = (hex: string): string => {
  if (hex === undefined) {
    console.log('hex:' + hex);
  }
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  return `${r}, ${g}, ${b}`;
};

export const getDifference = (value: number, prevValue: number): string | null =>
  prevValue !== 0 ? `${((Math.abs(value - prevValue) / prevValue) * 100).toFixed(0)}%` : '100%';

export const normalizeProp = (prop: string | number | [number, number]): string =>
  typeof prop === 'number' ? `${prop}px` : (Array.isArray(prop) && `${prop[0]}px ${prop[1]}px`) || prop.toString();

export const defineColorByPriority = (priority: Priority): string => {
  switch (priority) {
    case Priority.INFO:
      return 'var(--primary-color)';
    case Priority.LOW:
      return 'var(--success-color)';
    case Priority.MEDIUM:
      return 'var(--warning-color)';
    case Priority.HIGH:
      return 'var(--error-color)';
    default:
      return 'var(--success-color)';
  }
};

export const defineColorBySeverity = (severity: NotificationType | undefined, rgb = false): string => {
  const postfix = rgb ? 'rgb-color' : 'color';
  switch (severity) {
    case 'error':
    case 'warning':
    case 'success':
      return `var(--${severity}-${postfix})`;
    case 'info':
    default:
      return `var(--primary-${postfix})`;
  }
};

export const defineColorByButtonType = (buttontype: NotificationType | undefined, rgb = false): string => {
  const postfix = rgb ? 'rgb-color' : 'color';
  switch (buttontype) {
    case 'pending':
      return '#C50000'; //`var(--${buttontype}-${postfix})`;
    case 'assigned':
      return '#1A8BAE'; //`var(--${buttontype}-${postfix})`;
    case 'completed':
      return '#556AEB'; //`var(--${buttontype}-${postfix})`;
    default:
      return `var(--primary-${postfix})`;
  }
};

export const defineBGColorByButtonType = (buttontype: NotificationType | undefined, rgb = false): string => {
  const postfix = rgb ? 'rgb-color' : 'color';
  switch (buttontype) {
    case 'button':
      return hexToRGB('#6E82FE');
    case 'tag':
      return hexToRGB('#ff0000');
    case 'inform':
      return hexToRGB('#0000ff');
    case 'pending':
      return `var(--${buttontype}-${postfix})`; //hexToRGB('#ff0000');
    case 'assigned':
      return `var(--${buttontype}-${postfix})`;
    case 'completed':
      return `var(--${buttontype}-${postfix})`; //hexToRGB('#0000ff');
    default:
      return defineColorByButtonType(buttontype, rgb);
  }
};

export const defineBorderColorByButtonType = (buttontype: NotificationType | undefined, rgb = false): string => {
  const postfix = rgb ? 'rgb-color' : 'color';
  switch (buttontype) {
    case 'button':
      return '#7E92FE';
    case 'tag':
      return '#ff0000';
    case 'inform':
      return '#0000ff';
    default:
      return defineColorByButtonType(buttontype, rgb);
  }
};

export const defineFontColorByButtonType = (buttontype: NotificationType | undefined, rgb = false): string => {
  const postfix = rgb ? 'rgb-color' : 'color';
  switch (buttontype) {
    case 'button':
      return '#ffffff';
    case 'tag':
      return '#ff0000';
    case 'inform':
      return '#0000ff';
    default:
      return defineColorByButtonType(buttontype, rgb);
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mergeBy = (a: any[], b: any[], key: string): any[] =>
  a.filter((elem) => !b.find((subElem) => subElem[key] === elem[key])).concat(b);

export const getSmoothRandom = (factor: number, start: number): number => {
  const halfEnvelope = 1 / factor / 2;
  const max = Math.min(1, start + halfEnvelope);
  const min = Math.max(0, start - halfEnvelope);

  return Math.random() * (max - min) + min;
};

export const shadeColor = (color: string, percent: number): string => {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = parseInt(((R * (100 + percent)) / 100).toString());
  G = parseInt(((G * (100 + percent)) / 100).toString());
  B = parseInt(((B * (100 + percent)) / 100).toString());

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  const RR = R.toString(16).length == 1 ? '0' + R.toString(16) : R.toString(16);
  const GG = G.toString(16).length == 1 ? '0' + G.toString(16) : G.toString(16);
  const BB = B.toString(16).length == 1 ? '0' + B.toString(16) : B.toString(16);

  return '#' + RR + GG + BB;
};

export const hexToHSL = (hex: string): { h: number; s: number; l: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (result) {
    let r = parseInt(result[1], 16);
    let g = parseInt(result[2], 16);
    let b = parseInt(result[3], 16);
    (r /= 255), (g /= 255), (b /= 255);
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h, s;
    const l = (max + min) / 2;
    if (max == min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          h = 0;
      }
      h /= 6;
    }
    return {
      h,
      s,
      l,
    };
  } else {
    throw new Error('Non valid HEX color');
  }
};

export const formatNumberWithCommas = (value: number): string => {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
export const msToH = (ms: number): number => Math.floor(ms / 3600000);

export const hToMS = (h: number): number => h * 3600000;

export const getPaymentCardTypeIcon = (type: string): string | null => {
  switch (type) {
    case 'visa':
      return visa;
    case 'mastercard':
      return mastercard;
    case 'maestro':
      return maestro;
    case 'amex':
      return 'amex';
    case 'discover':
      return 'discover';
    case 'diners':
      return 'diners';
    case 'jcb':
      return 'jcb';
    case 'unionpay':
      return 'unionpay';
    default:
      return null;
  }
};

export const mapBadgeStatus = (status: BaseBadgeProps['status']): Severity => {
  if (!status || status === 'default' || status === 'processing') {
    return 'info';
  }

  return status;
};

export const isCompletedStatus = (obj: Record<string, any>, keys: string[]): 1 | 9 => {
  for (const key of keys) {
    if (obj && !(key in obj)) {
      return 1;
    }
  }

  //console.log(obj, keys);

  for (const key of keys) {
    const value = obj?.[key];
    if (
      (typeof value === 'string' && value === '') ||
      (typeof value === 'number' && value === 0) ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === 'object' && Object.values(value).some((v: any) => v === ''))
    ) {
      return 1;
    }
  }

  return 9;
};

// export const isCompletedStatus = (obj: Record<string, any>, keys: string[]): 0 | 1 | 9 => {
//   let hasValue = false;
//
//   for (const key of keys) {
//     if (key in obj) {
//       const value = obj[key];
//       if (
//         (typeof value === 'string' && value !== '') ||
//         (typeof value === 'number' && value !== 0) ||
//         (Array.isArray(value) && value.length !== 0) ||
//         (typeof value === 'object' && !isEmptyObject(value))
//       ) {
//         hasValue = true;
//       }
//     }
//   }
//
//   if (hasValue) {
//     return 0;
//   } else if (keys.every((key) => !(key in obj))) {
//     return 1;
//   } else {
//     return 9;
//   }
// };
//
// // Helper function to check if an object is empty
// function isEmptyObject(obj: Record<string, any>): boolean {
//   for (const key in obj) {
//     if (obj.hasOwnProperty(key)) {
//       return false;
//     }
//   }
//   return true;
// }

export const loadScript = (videoScript: any, setProcess: any, setBRefresh: any) => {
  videoScript.current = document.createElement('script');

  videoScript.current.src = 'https://embed.cloudflarestream.com/embed/sdk.latest.js';
  //script.defer = true;
  videoScript.current.type = 'text/javascript';
  videoScript.current.async = false;

  videoScript.current.onload = () => {
    //selectTask(0);
    // @ts-ignore
    const player = Stream(document.getElementById('stream-player'));

    player.addEventListener('play', (e: any) => {
      // console.log('playing!' + e.toString());
    });
    player.addEventListener('canplay', (e: any) => {
      // console.log('can play!' + e.toString());
      setProcess(0);
      setBRefresh(false);
      try {
        //if (document.body.contains(videoScript.current))
        document.body.removeChild(videoScript.current);
      } catch {}
    });
    player.addEventListener('loadedmetadata', (e: any) => {
      if (e.target === null) return;
      // console.log('loadedmetadata!' + e.target.videoWidth + '/' + e.target.videoHeight);
    });
    player.play().catch(() => {
      // console.log('playback failed, muting to try again');
      player.muted = true;
      //player.play();
    });
    player.addEventListener('error', (e: any) => {
      // console.log('error!' + e.toString());
      // selectTa        // selectTask(0);sk(0);
    });
    //player.play();
  };
  document.body.appendChild(videoScript.current);
};

interface PDFDrawProps {
  text: string;
  x: number;
  y: number;
  font: any;
  fontSize: number;
  page: any;
  yOffset: number;
  isInitXPos?: number;
  color: any;
  maxLength: number;
  secondMaxLength?: number;
}
function calculateDynamicMaxLength(
  text: string,
  maxLength: number,
  secondMaxLength: number | null,
  count: number,
): number {
  // 한글, 영어, 숫자 포함 여부를 확인
  const hasKorean = /[가-힣]/.test(text);
  const hasEnglishOrNumber = /[A-Za-z0-9]/.test(text);

  // 영어나 숫자가 포함되어 있고 한글도 포함되어 있는 경우
  if (hasEnglishOrNumber && hasKorean) {
    const nonKoreanCharRatio = text.split('').filter((char: string) => !char.match(/[가-힣]/)).length / text.length;
    const koreanCharRatio = 1 - nonKoreanCharRatio;
    // 영어와 숫자는 한글의 절반 크기로 가정하여 조정
    const adjustedMaxLength = Math.floor(maxLength * koreanCharRatio + maxLength * nonKoreanCharRatio * 2);
    return count > 0 && secondMaxLength ? Math.min(secondMaxLength, adjustedMaxLength) : adjustedMaxLength;
  }
  // 오직 한글만 있는 경우 또는 영어/숫자만 있는 경우
  else {
    // 기본 maxLength를 사용하거나, 두 번째 줄 이상에서 secondMaxLength가 정의된 경우 해당 값을 사용
    return count > 0 && secondMaxLength ? secondMaxLength : maxLength;
  }
}
// export const drawTextWithLineBreaks = ({
//   text,
//   x,
//   y,
//   font,
//   fontSize,
//   page,
//   yOffset,
//   isInitXPos,
//   color,
//   maxLength,
//   secondMaxLength,
// }: PDFDrawProps) => {
//   let remainingText = text;
//   let count = 0;
//   while (remainingText.length > 0) {
//     let line = remainingText.slice(0, count > 0 && secondMaxLength ? secondMaxLength : maxLength);
//     // const dynamicMaxLength = calculateDynamicMaxLength(remainingText, maxLength, secondMaxLength, count);
//     // let line = remainingText.slice(0, dynamicMaxLength);
//     const lineBreakIndex = line.indexOf('\n');
//
//     if (lineBreakIndex !== -1) {
//       // 줄바꿈 문자가 발견되면 해당 위치까지만 출력하고 나머지는 새로운 문단으로 처리
//       line = line.slice(0, lineBreakIndex);
//     }
//
//     const xPos = count > 0 && isInitXPos ? isInitXPos : x;
//     page.drawText(line, { x: xPos, y, font, size: fontSize, color });
//
//     if (lineBreakIndex !== -1) {
//       // 줄바꿈 문자가 발견된 경우 해당 위치까지 제거하고 나머지를 다음 루프에서 처리
//       remainingText = remainingText.slice(lineBreakIndex + 1); // +1을 추가하여 '\n' 문자를 포함하지 않도록 처리
//     } else {
//       // 줄바꿈 문자가 없는 경우에는 기존 코드와 같이 maxLength 또는 secondMaxLength에 따라 잘라냄
//       remainingText = remainingText.slice(count > 0 && secondMaxLength ? secondMaxLength : maxLength);
//     }
//     // remainingText = remainingText.slice(count > 0 && secondMaxLength ? secondMaxLength : maxLength);
//     count++;
//     // y 좌표를 조정.
//     y += yOffset;
//   }
// };
export const drawTextWithLineBreaks = ({
  text,
  x,
  y,
  font,
  fontSize,
  page,
  yOffset,
  isInitXPos,
  color,
  maxLength, // 350
  secondMaxLength,
}: PDFDrawProps) => {

  const lines = wrapText(text, maxLength);

  if( Number.isNaN(y)) {
    console.log('y is NaN');
  }
  lines.forEach((line, index) => {
    page.drawText(line, { x: x, y:y-fontSize*index*1.5+yOffset, font, size: fontSize, color });
  })
  return lines.length;
  /*
  let remainingText: string = text;
  let count = 0;
  let indentCount = 0;
  while (remainingText.length > 0) {
    const initialSliceLength = count > 0 && secondMaxLength ? secondMaxLength : maxLength;
    let line = remainingText.slice(0, initialSliceLength);
    const lineBreakIndex = line.indexOf('\n');

    if (lineBreakIndex !== -1) {
      line = line.slice(0, lineBreakIndex);
    } else {
      // 영어와 숫자의 갯수를 계산
      const englishCount = (line.match(/[A-Za-z]/g) || []).length;
      const numberCount = (line.match(/[0-9]/g) || []).length;

      // 영어는 2배, 숫자는 1.5배로 추가 처리
      // const additionalLength = englishCount * 2 - englishCount + Math.floor(numberCount * 1.2 - numberCount);
      const additionalLength =
        englishCount && numberCount
          ? englishCount
          : numberCount && !englishCount
          ? numberCount >= 25
            ? numberCount
            : Math.ceil(numberCount / 2)
          : // numberCount
            englishCount;

      const adjustedLength = initialSliceLength + Math.min(additionalLength, 7);
      line = remainingText.slice(0, !!(englishCount || numberCount) ? adjustedLength : initialSliceLength);
      console.log(
        englishCount,
        numberCount,
        'numberCount',
        additionalLength,
        initialSliceLength,
        adjustedLength,
        Math.floor(numberCount * 1.5) - numberCount,
      );
      console.log(line, 'line');
    }

    const xPos: number = count > 0 && isInitXPos ? isInitXPos : x;
    page.drawText(line, { x: xPos, y, font, size: fontSize, color });

    if (lineBreakIndex !== -1) {
      // 줄바꿈 문자가 발견된 경우, 들여쓰기를 위한 빈 여백 추가
      remainingText = '        ' + remainingText.slice(lineBreakIndex + 1);
      indentCount++;
    } else {
      remainingText = remainingText.slice(line.length);
    }

    count++;
    y += yOffset; // y 좌표 조정, 페이지의 상단에서 하단으로 이동하는 경우 y 값을 감소
  }
  return indentCount;
  */
};

interface GrowthTasksTotalScore {
  cognition: number;
  socialEmotional: number;
  language: number;
  exercise: number;
}

export function evaluateDevelopment(scores: GrowthTasksTotalScore): string {
  // 발달 상태 정의
  const developmentStatusOrder: { [key: number]: string } = {
    2: '정상 발달',
    1: '관찰이 필요한 발달',
    0: '도움이 필요한 발달',
  };

  // 영역 이름 정의
  const areas: { [key: string]: string } = {
    cognition: '인지영역',
    socialEmotional: '사회정서영역',
    language: '언어영역',
    exercise: '운동영역',
  };

  // 각 발달 상태별 영역을 저장할 객체 초기화
  const statusResults: { [key: string]: string[] } = {
    '정상 발달': [],
    '관찰이 필요한 발달': [],
    '도움이 필요한 발달': [],
  };

  // scores 객체를 순회하며 각 영역별로 해당하는 발달 상태에 영역 이름 추가
  Object.entries(scores).forEach(([area, score]) => {
    //console.log(area+'->'+score);
    if( !(score >= 0 && score <= 2) ) score = 1;
    const areaName = areas[area];
    const status = developmentStatusOrder[score];
    statusResults[status].push(areaName);  
  });

  // 결과 문자열 생성
  const resultSentences: string[] = [];
  Object.entries(developmentStatusOrder)
    .sort((a, b) => parseInt(b[0]) - parseInt(a[0])) // 점수를 기준으로 내림차순 정렬
    .forEach(([_, status]) => {
      if (statusResults[status].length > 0) {
        resultSentences.push(`${statusResults[status].join(', ')}에서 ${status}로 평가되었`);
      }
    });

  return `세부 영역으로, ${resultSentences.join('고 ')}습니다.`;
}

// export const interpretValues = (values: Record<string, number>) => {
//   const interpretations: Record<string, string> = {
//     cognition: '인지',
//     language: '언어',
//     exercise: '운동',
//     socialEmotional: '사회정서',
//   };
//
//   const zeroArray = [];
//   const oneArray = [];
//   const twoArray = [];
//
//   const interpretKeys = [];
//
//   for (const key in values) {
//     // console.log(values, key);
//     // console.log(values, key);
//     if (values[key] === 0) {
//       zeroArray.push({ [key]: values[key] });
//     } else if (values[key] === 1) {
//       oneArray.push({ [key]: values[key] });
//     } else {
//       twoArray.push({ [key]: values[key] });
//     }
//   }
//   const result =
//     zeroArray.length === 0 && oneArray.length !== 0
//       ? oneArray
//       : oneArray.length === 0 && twoArray.length !== 0
//       ? twoArray
//       : zeroArray;
//
//   for (const item of result) {
//     interpretKeys.push(interpretations[Object.keys(item)[0]]);
//   }
//   const comment =
//     zeroArray.length === 0 && oneArray.length !== 0
//       ? '관찰이 필요할 것으로 보이며,'
//       : oneArray.length === 0 && twoArray.length !== 0
//       ? '가정에서 지속적인 특별한 어려움이 나타나지 않으며,'
//       : '전문가의 도움이 필요할 것으로 보이며,';
//
//   return `${interpretKeys.join(' 및 ')}과(와) 관련하여 ${comment}`;
// };

export const interpretValues = (values: Record<string, number>) => {
  const interpretations: Record<string, string> = {
    cognition: '인지',
    language: '언어',
    exercise: '운동',
    socialEmotional: '사회정서',
  };

  const zeroArray = [];
  const oneArray = [];
  const twoArray = [];

  for (const key in values) {
    if (values[key] === 0) {
      zeroArray.push({ [key]: values[key] });
    } else if (values[key] === 1) {
      oneArray.push({ [key]: values[key] });
    } else {
      twoArray.push({ [key]: values[key] });
    }
  }

  let result: any;
  let comment: any;

  if (zeroArray.length > 0) {
    result = zeroArray;
    comment = '전문가의 도움이 필요할 것으로 보이며,';
  } else if (oneArray.length > 0) {
    result = oneArray;
    comment = '관찰이 필요할 것으로 보이며,';
  } else if (twoArray.length > 0) {
    result = twoArray;
    comment = '가정에서 지속적인 특별한 어려움이 나타나지 않으며,';
  }

  const interpretKeys = result.map((item: any) => interpretations[Object.keys(item)[0]]);

  return `${interpretKeys.join(' 및 ')}과(와) 관련하여 ${comment}`;
};

export const calcGrowth = (value: number) => {
  if (value === 0) {
    return '도움이 필요';
  } else if (value === 1) {
    return '관찰이 필요';
  } else {
    return '정상 발달';
  }
};

export const getGrowthResult = (value: number) => {
  if (value === 0) {
    return '도움이 필요한 발달';
  } else if (value === 1) {
    return '관찰이 필요한 발달';
  } else {
    return '정상 발달';
  }
};

export const getGrowth = (value: number) => {
  if (value === 0) {
    return '도움이 필요한';
  } else if (value === 1) {
    return '관찰이 필요한';
  } else {
    return '정상';
  }
};

export function generateRandomCode(length: number) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let code = '';

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    code += characters.charAt(randomIndex);
  }

  return code;
}

export async function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = reject;
    reader.readAsArrayBuffer(blob);
  });
}

export async function serializeBlob(blob: Blob): Promise<string> {
  const arrayBuffer = await blobToArrayBuffer(blob);

  const serializedData = JSON.stringify({
    type: blob.type,
    data: Array.from(new Uint8Array(arrayBuffer)),
  });
  return serializedData;
}

export async function deserializeBlob(serializedData: string): Promise<Blob> {
  const parsedData = JSON.parse(serializedData);
  const arrayBuffer = new ArrayBuffer(parsedData.data.length);
  const uint8Array = new Uint8Array(arrayBuffer);
  for (let i = 0; i < parsedData.data.length; i++) {
    uint8Array[i] = parsedData.data[i];
  }
  return new Blob([arrayBuffer], { type: parsedData.type });
}
type TaskData = {
  title: string;
  result: number;
};

type ProcessedData = {
  targetId: string;
  data: TaskData[];
};
export function processData(tasks: any[], tags: any) {
  if (!tasks?.length) {
    return;
  }
  const processedData: any = [];
  let name = '';
  let id = '';
  let sex = '';
  let bdate;
  let profileImg = '';

  tasks.forEach((task) => {
    const taskData = task?.data === '' ? {} : typeof task.data === 'string' ? JSON.parse(task.data) : task.data;
    const targetData =
      task?.data === ''
        ? {}
        : typeof task?.target?.data === 'string'
        ? JSON.parse(task?.target?.data)
        : task?.target?.data;
    const targetId = task.target.id;
    name = task.target.title;
    id = targetId;
    sex = targetData.sex === 1 ? '여' : '남';
    bdate = getMonth(targetData.bdate);
    profileImg = targetData?.profileImg ?? '';

    const exmonthExists = processedData.some((item: any) => item.exMonth === taskData?.exmonth);

    console.log(name);

    if (!exmonthExists) {
      processedData.push({
        exMonth: taskData?.exmonth,
        status: task?.status,
        pdfFileUrl: taskData?.pdfFileUrl,
        pdfImages: taskData?.pdfImages,
        executedAt: task.executedAt ? Dates.format(task.executedAt, 'YYYY.MM.DD(ddd)') : '',
        bdate: targetData.bdate,
        name,
        title: taskData?.exmonth,
        result: task?.category ? taskData[task.category]?.growthResult ?? null : null,
      });
    }
  });

  return { name, id, sex, bdate, profileImg, history: processedData };
}

export const getTargetResultData = (tasks: any[], tags: any) => {
  if (!tasks?.length) {
    return;
  }
  let name = '';
  let id = '';
  let sex = '';
  let bdate;
  const processedData: any = [];

  tasks.forEach((task) => {
    const taskData = task.data === '' ? {} : typeof task.data === 'string' ? JSON.parse(task.data) : task.data;
    const targetData =
      task.data === ''
        ? {}
        : typeof task?.target?.data === 'string'
        ? JSON.parse(task?.target?.data)
        : task?.target?.data;
    const targetId = task.target.id;

    id = targetId;
    name = task.target.title;
    sex = targetData.sex === 1 ? '여' : '남';
    bdate = getMonth(targetData.bdate);

    id = targetId;

    const exmonthExists = processedData.some((item: any) => item.exMonth === taskData?.exmonth);

    if (!exmonthExists) {
      processedData.push({
        exMonth: taskData?.exmonth,
        status: task?.status,
        pdfFileUrl: taskData?.pdfFileUrl,
        pdfImages: taskData?.pdfImages,
        growthResult: task?.category ? taskData[task?.category]?.growthResult : 0,
        statusTags: task?.category ? taskData[task?.category]?.statusTag : 0,
      });
    }
  });

  return { name, id, sex, bdate, history: processedData };
};

export const getChartData = (targetData: any) => {
  if (!targetData?.length) {
    return [];
  }

  const resultMap = new Map();

  targetData.forEach((point: any) => {
    if (point.executedAt) {
      const xValue = point.title;
      const yValue = point.result;
      resultMap.set(xValue, yValue);
    }
  });

  const xValues: any = [];
  const yValues: any = [];

  resultMap.forEach((yValue, xValue) => {
    xValues.push(xValue);
    yValues.push(yValue);
  });

  return [xValues, yValues];
};

export function generateChartData(historyData: any, xAxisLabel: string[]) {
  const resultArray = xAxisLabel.map((label, index) => {
    const matchingHistory = historyData.find((data: any) => data.exMonth === parseInt(label));
    if (matchingHistory && matchingHistory.status === 2) {
      return matchingHistory.growthResult;
    } else {
      return null;
    }
  });

  return resultArray;
}

export function generateUniqueFileName() {
  const array = new Uint8Array(8); // 8바이트 길이의 배열 생성
  window.crypto.getRandomValues(array); // 배열에 무작위 값 채우기

  // 배열을 16진수 문자열로 변환
  let randomString = '';
  for (let i = 0; i < array.length; i++) {
    randomString += array[i].toString(16);
  }

  const timestamp = new Date().getTime();
  return `${timestamp}-${randomString}`;
}

export async function sendEmail(data: any, key:any): Promise<string> {
  
  // @ts-ignore
  //const emaildata = JSON.parse(params);

  try{
    /*
    const requestOptions = {
      method: 'GET',
      headers: { 
        'Content-Type': 'application/json',
      },
    };
    //const apiServer = 'http://localhost:3000/'; // 
    const apiServer = 'https://8bc4t1e6pe.execute-api.ap-northeast-2.amazonaws.com/';
    await fetch(apiServer, requestOptions)
        .then(response => response.json())
        .then(data => console.log('return:'+data));
    */
   /*
    const requestOptions = {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: 'email', data: params }),
    };
    //const apiServer = 'https://d.mbisq.com/dev/sendMail'; // 
    const apiServer = 'https://w9ka9vn9xd.execute-api.ap-northeast-2.amazonaws.com/dev/sendMail';
    await fetch(apiServer, requestOptions)
        .then(response => response.json())
        .then(data => console.log('return:'+data));
    */
   /**
    https://kht3cmice5.execute-api.ap-northeast-2.amazonaws.com/ica


    curl -X POST 'https://11sawjgicf.execute-api.ap-northeast-2.amazonaws.com/default/icentermail'     -H 'Content-Type: application/json'     -d '{
        "Source": "seoul-i@seoul-i.kr", 
        "Destination": {
            "ToAddresses": ["blueton@gmail.com"] 
        },
        "Message": {
            "Subject": { "Data": "222Test Email from AWS SES" },
            "Body": { "Text": { "Data": "This is the body text of the email." }}
        }
    }'

    curl -X POST 'https://kht3cmice5.execute-api.ap-northeast-2.amazonaws.com/default/icentermail'     -H 'Content-Type: application/json'     -d '{
        "Source": "seoul-i@seoul-i.kr", 
        "Destination": {
            "ToAddresses": ["blueton@gmail.com"] 
        },
        "Message": {
            "Subject": { "Data": "222Test Email from AWS SES" },
            "Body": { "Text": { "Data": "This is the body text of the email." }}
        }
    }'params

    */
//params.Source, // 검증된 발신자 이메일 주소
//params.Destinations,
// 이메일 제목
// HTML 형식의 이메일 본문
/*
    const param = {
      Source: params.Source, //"seoul-i@seoul-i.kr", 
      Destination: {
        ToAddresses: params.Destinations //["blueton@gmail.com"] 
      },
      Message: {
        Subject: {
          Data: 'HTML Email Test 2' 
        },
        Body: {
          Html: {
            Data: `
              <html>
                  <head></head>
                  <body>
                      <h1>Hello, World!</h1>
                      <p>This is a test email with a link. Click <a href="https://example.com">here</a> to visit our website.</p>
                  </body>
              </html>
            ` 
          },
        },
      },
    };
    */
/**
 * `{ "Source": "seoul-i@seoul-i.kr", "Destination": { "ToAddresses": ["blueton@gmail.com"] }, "Message": { "Subject": { "Data": "123Test Email from AWS SES" }, 
      "Body": { "Html": {"Data": "
          <html>
              <head></head>
              <body>
                  <h1>Hello, World!</h1>
                  <p>This is a test email with a link. Click <a href="https://example.com">here</a> to visit our website.</p>
              </body>
          </html>
        "}}}}`


"\n            <html>\n                <head>\n                </head>\n                <body>\n                <p style=\"color: #333;\">\n                    안녕하세요. 서울아이발달지원센터입니다. <br/><br/>\n                    우선 [찾아가는 어린이집 발달선별검사]에 참여해 주셔서 깊은 감사를 드립니다. [찾아가는 어린이집 발달선별검사]는 영유아 발달 전문가가 직접 어린이집에 방문하여 유아와 상호작용하며 해당 월령에 꼭 필요한 과제들을 시행해 보고 발달 정도를 체크해 보는 검사입니다.\n                  <br/><br/>\n                    첨부된 보고서를 통해 자녀의 검사 결과를 받아보실 수 있으며, 결과와 관련하여 상담을 원하실 경우 서울아이발달지원센터 홈페이지(<a style=\"color:#333;\" href=\"https://www.seoul-i.kr\">www.seoul-i.kr</a>)에서 상담 예약을 하실 수 있습니다. 또한 양육에 도움이 될 만한 안내지를 다운로드할 수 있는 링크를 메일 하단에 첨부하였으니 확인해 주시기 바랍니다.\n                  <br/><br/>\n                    감사합니다.<br/>\n                  서울아이발달지원센터 드림.\n                  <br/><br/>     \n                </p>\n          <table border=\"1\" style=\"border-collapse: collapse; border-color:#444; width: 400px\">\n    <tr>\n    <td colspan=\"2\" style=\"color:#000; padding-left:8px\" bgcolor= \"#eee\">첨부</td>\n    </tr>\n    <tr ><td style=\"color:#000; padding-left:8px\" bgcolor= \"#ccc\">1227테스트의 검사결과</td><td style=\"width:30%;text-align: center;\" bgcolor= \"#ccc\"> <a style=\"color:#00f; text-decoration:underline\" href=\"https://mbi-pdf-assets-bucket.s3.ap-northeast-2.amazonaws.com/pdf/1711879059045-f4ef6842f6d6a75\">열기</a></td></tr><tr><td style=\"color:#333; padding-left:8px\">발달가이드 (18-24개월) </td><td style=\"width:30%;text-align: center;\"><a style=\"color:#00f;\" href=\"https://ict.liflo.io/link/2\">열기</a></td></tr><tr><td style=\"color:#333; padding-left:8px\">발달가이드 (24-30개월) </td><td style=\"width:30%;text-align: center;\"><a style=\"color:#00f;\" href=\"https://ict.liflo.io/link/3\">열기</a></td></tr><tr><td style=\"color:#333; padding-left:8px\">발달상담가이드 (24-36개월) </td><td style=\"width:30%;text-align: center;\"><a style=\"color:#00f;\" href=\"https://ict.liflo.io/link/6\">열기</a></td></tr></table></body></html>"
 */
    if(false){
    const start = "\">열기</a></td></tr><tr>";
    const nStart = data.Message.Body.Html.Data.indexOf(start); 
    const end = "</td></tr></table></body></html>"
    const nEnd = data.Message.Body.Html.Data.indexOf(end); 
    const body1 = data.Message.Body.Html.Data.substring(0, nStart+start.length);
    const body2 = data.Message.Body.Html.Data.substring(nEnd);
    if( data.Message.ConfigurationSetName === undefined)
      data.Message.ConfigurationSetName = 'EmailTrackingConfig';
    data.Message.Body.Html.Data = body1 + "<td style=\"color:#333; padding-left:8px\">발달 선별검사 가이드</td><td style=\"width:30%;text-align: center;\"><a style=\"color:#00f;\" href=\"https://ica.mbisq.com/pdf/guide.pdf\">열기</a>" + body2;
    }

    const requestOptions = {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
      },
      //body: emaildata
      //body: '{"Source":"seoul-i@seoul-i.kr","Destinations":["blueton@gmail.com"],"RawMessage":{"Data":"Subject: [서울아이발달지원센터] 발달선별검사 보고서 "}}'
      body: JSON.stringify(data)
    };
    //const apiServer = 'https://kht3cmice5.execute-api.ap-northeast-2.amazonaws.com/default/icentermail';
    const apiServer = 'https://11sawjgicf.execute-api.ap-northeast-2.amazonaws.com/default/icentermail';
    await fetch(apiServer, requestOptions)
        .then(response => response.json())
        .then( async(data:any) => {
          console.log('return:'+data);
          const requestOptions = {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
            },
          };
          if( false && key !== null ){
            const apiServer = //'http://localhost:8787/getMail/'+key; 
            'https://ict.mbisq.com/popMail/'+key; 
              return await fetch(apiServer, requestOptions)
              .then(response => {
              console.log(response);
              if( response.ok === true)
              return response.json();
              else
              return {result:undefined};
              })
              .then(async(data:any) => {
              return data;
              });  
          }
      
        });

    return '';
  } catch (err) {
    throw err;
  }
}

export async function uploadImageToS3(imageData: string): Promise<string> {
  /*
  const s3 = new AWS.S3();
  const fileName = `images/${generateUniqueFileName()}`;
  const bucketName = 'mbi-pdf-assets-bucket';
  const base64Data = imageData.replace(/^data:image\/\w+;base64,/, '');
  const buffer = Buffer.from(base64Data, 'base64');

  const params = {
    Bucket: bucketName,
    Key: fileName,
    Body: buffer,
    ACL: 'public-read',
    ContentEncoding: 'base64',
    ContentType: 'image/jpeg',
  };

  try {
    const data = await s3.upload(params).promise();
    return data.Location; // 업로드된 파일의 공용 URL 반환
  } catch (err) {
    throw err;
  }
  */
  try {
    const fileName = `images/${generateUniqueFileName()}`;
    //const base64Data = imageData.replace(/^data:image\/\w+;base64,/, '');
    //const buffer = Buffer.from(base64Data, 'base64');
  
    const requestOptions = {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: fileName, data: imageData }),
    };
    //const apiServer = 'http://localhost:3000/dev/saveJpg'; // 
    const apiServer = 'https://w9ka9vn9xd.execute-api.ap-northeast-2.amazonaws.com/dev/saveJpg';
    await fetch(apiServer, requestOptions)
        .then(response => response.json())
        .then(data => console.log('return:'+data));
        
    return 'https://mbi-pdf-assets-bucket.s3.ap-northeast-2.amazonaws.com/' + fileName; // 업로드된 파일의 공용 URL 반환
  } catch (err) {
    throw err;
  }
}

export async function uploadPDFToS3(pdfBuffer: Buffer, pdfFileName: string): Promise<string> {
  /*
  const s3 = new AWS.S3();
  const bucketName = 'mbi-pdf-assets-bucket';

  const params = {
    Bucket: bucketName,
    Key: pdfFileName,
    Body: pdfBuffer,
    ContentType: 'application/pdf',
  };

  try {
    const data = await s3.upload(params).promise();
    return data.Location; // 업로드된 파일의 공용 URL 반환
  } catch (err) {
    throw err;
  }
  */
  
  try {
    const requestOptions = {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    };
    //console.log('write userData:'+requestOptions.body)
    //const apiServer = 'https://d.mbisq.com/getPdfUrl?id='+pdfFileName; // 
    const apiServer = 'https://6e85u9r4x8.execute-api.ap-northeast-2.amazonaws.com/getPdfUrl?id='+pdfFileName; 

    console.log('apiServer:'+apiServer);
    await fetch(apiServer, requestOptions)
        .then(response => {
          console.log(response);
          if( response.ok === true)
            return response.json();
          else
            return {result:undefined};
          })
        .then(data => {
            console.log('pdf url:', data);
            if( data.uploadUrl !== undefined){
              const requestOptions = {
                method: 'PUT',
                headers: { 'Content-Type': 'application/pdf' },
                body: pdfBuffer,
              };
              fetch(data.uploadUrl, requestOptions)
                .then(response => {
                  console.log(response)
                })  
            }
        });

    return 'https://mbi-pdf-assets-bucket.s3.ap-northeast-2.amazonaws.com/pdf/'+pdfFileName; // 업로드된 파일의 공용 URL 반환

    /*
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(JSON.stringify({ filname: pdfFileName, receivedData: pdfBuffer })),
    };
    const apiServer = 'http://localhost:3000/dev/savePdf'; // 
    await fetch(apiServer, requestOptions)
        .then(response => response.json())
        .then(data => console.log('return:'+data));
    */
    return pdfFileName; // 업로드된 파일의 공용 URL 반환
  } catch (err) {
    throw err;
  }
}

export async function uploadZipToS3(pdfBuffer: Buffer, zipFileName: string): Promise<string> {
  
  try {
    const requestOptions = {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    };
    //console.log('write userData:'+requestOptions.body)
    //const apiServer = 'https://d.mbisq.com/getPdfUrl?id='+pdfFileName; // 
    const apiServer = 'https://6e85u9r4x8.execute-api.ap-northeast-2.amazonaws.com/getFileUrl?id='+zipFileName; 

    console.log('apiServer:'+apiServer);
    await fetch(apiServer, requestOptions)
        .then(response => {
          console.log(response);
          if( response.ok === true)
            return response.json();
          else
            return {result:undefined};
          })
        .then(data => {
            console.log('pdf url:', data);
            const requestOptions = {
              method: 'PUT',
              headers: { 'Content-Type': 'application/zip' },
              body: pdfBuffer,
            };
            fetch(data.uploadUrl, requestOptions)
              .then(response => {
                console.log(response)
              })
        });

    return 'https://mbi-pdf-assets-bucket.s3.ap-northeast-2.amazonaws.com/zip/'+zipFileName; // 업로드된 파일의 공용 URL 반환

  } catch (err) {
    throw err;
  }
}


export const findAndAddMissingTasks = (dataMonth: string[], serverTasks: any[]) => {
  // 서버 데이터에서 태스크 제목 추출
  const serverTaskTitles = serverTasks.map((task) => task.title);

  // dataMonth에서 서버 데이터에 없는 태스크 찾기
  const missingTasks = dataMonth.filter((task) => !serverTaskTitles.includes(task));

  return missingTasks;
};

export const findMissingTitles = (inputArray: any[], key: number): any[] => {
  // Retrieve all keys from onlineTasks and find the highest and lowest key
  const keys = Object.keys(onlineTasks).map(Number);
  const highestKey = Math.max(...keys);
  const lowestKey = Math.min(...keys);

  // Adjust the key based on the range of available keys in onlineTasks
  const effectiveKey = key < lowestKey ? lowestKey : key > highestKey ? highestKey : key;

  // Retrieve the list of tasks for the effective key
  const tasks = onlineTasks[effectiveKey] || [];

  // If inputArray is empty, return all tasks for the effective key
  if (inputArray.length === 0) {
    return tasks.map((task) => ({ title: task.title, category: task.category }));
  }

  // Extract the titles from the input array
  const input_titles = new Set(inputArray.map((item: any) => item.title));

  // Find missing titles in the tasks
  const missing_titles = tasks
    .filter((task) => !input_titles.has(task.title))
    .map((task) => ({ title: task.title, category: task.category }));

  return missing_titles;
};


// 문자 유형별 평균 너비를 정의합니다.
const characterWidths: { [key: string]: number } = {
  'default': 5,  // 기본값, 정의되지 않은 문자에 사용
  'space': 3,    // 공백
  'comma': 3,    // 콤마
  'special': 4,  // 기타 특수문자
  'number': 5,   // 숫자
  'english': 5,  // 영문자
  'korean': 10,  // 한글
};

const wrapText = (text: string, maxWidth: number): string[] =>{
  const lines: string[] = [];
  let currentLine = '';
  let currentLineWidth = 0;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const charWidth = measureTextWidth(char);

    if (char === '\n') {
      // 명시적인 라인 브레이크 처리
      lines.push(currentLine);
      currentLine = '';
      currentLineWidth = 0;
    } else if (currentLineWidth + charWidth > maxWidth) {
      // 현재 라인을 lines 배열에 추가하고 새로운 라인을 시작합니다.
      lines.push(currentLine);
      currentLine = char;
      currentLineWidth = charWidth;
    } else {
      // 현재 라인에 캐릭터를 추가합니다.
      currentLine += char;
      currentLineWidth += charWidth;
    }
  }

  // 마지막으로 남은 텍스트를 추가합니다.
  if (currentLine.length > 0) {
    lines.push(currentLine);
  }

  return lines;
}

const wrapTextByWord = (text: string, maxWidth: number): string[] =>{
  // 먼저, 직접적인 라인 브레이크를 기준으로 텍스트를 분리합니다.
  const segments = text.split('\n');
  let wrappedLines: string[] = [];

  segments.forEach(segment => {
    // 각 세그먼트를 단어 단위로 더 세밀하게 나누어 처리합니다.
    wrappedLines = wrappedLines.concat(wrapTextEach(segment, maxWidth));
  });

  return wrappedLines;
}

// 텍스트 래핑 함수
const wrapTextEach = (text: string, maxWidth: number): string[] => {
  const lines: string[] = [];
  let currentLine = '';

  const words = text.split(' ');
  words.forEach(word => {
    const newLine = currentLine.length ? `${currentLine} ${word}` : word;
    const lineWidth = measureTextWidth(newLine); // 텍스트 너비 측정 함수 (구현 필요)
    if (lineWidth < maxWidth) {
      currentLine = newLine;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  });

  lines.push(currentLine); // 마지막 라인 추가
  return lines;
};
// 텍스트 너비 측정 함수
function measureTextWidth(text: string): number {
  let width = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (char === ' ') {
      width += characterWidths['space'];
    } else if (char === ',') {
      width += characterWidths['comma'];
    } else if ('0' <= char && char <= '9') {
      width += characterWidths['number'];
    } else if (('A' <= char && char <= 'Z') || ('a' <= char && char <= 'z')) {
      width += characterWidths['english'];
    } else if (/[\u3131-\uD79D]/ugi.test(char)) { // 한글 유니코드 범위 검사
      width += characterWidths['korean'];
    } else if (/[!@#$%^&*()_+|~=`{}\[\]:";'<>?,.\/\\-]/.test(char)) {
      width += characterWidths['special'];
    } else {
      width += characterWidths['default'];
    }
  }
  return width;
}
